import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { styled as MUIStyled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeepResearchProgress from '../chat/DeepResearchProgress';
import PanoramaProgress from '../chat/PanoramaProgress';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CircularProgress from '@mui/material/CircularProgress';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
// docx generation now handled by backend
import { saveAs } from 'file-saver';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArticleIcon from '@mui/icons-material/Article';
import TooltipBase, { tooltipClasses } from '@mui/material/Tooltip';
import ShareModal from './ShareModal';
import GeneratedFilePreview from '../chat/GeneratedFilePreview';
import MessageComposerPreview from '../chat/MessageComposerPreview';
import PanoramaConfirmation from '../chat/PanoramaConfirmation';
import PanoramaDebug from '../chat/PanoramaDebug';
import ContentItemErrorBoundary from './ContentItemErrorBoundary';
import CodeExecutionBlock from '../chat/CodeExecutionBlock';
import ToolActionBlock from '../chat/ToolActionBlock';
import SearchResultsBlock from '../chat/SearchResultsBlock';
import ExtractingFilesBlock from '../chat/ExtractingFilesBlock';
import ThinkingGroup from '../chat/ThinkingGroup';
import { computeThinkingRuns, groupedIndexSet, thinkingBurstPending, hasVisibleItemsAfter, trailingThinkingRunActive } from '../../utils/thinkingGroup';
import { Reference, GeneratedFile, FileRef, FileRefContentItem, ContentItem, TextContentItem, FileContentItem, CodeExecutionContentItem, ToolActionContentItem, SearchResultsContentItem } from '../../shared/interfaces';
import LoadingMessageExtracted from './LoadingMessage';
import ErrorMessageExtracted from './ErrorMessage';
import ToolCallsDebugPanel from '../chat/ToolCallsDebugPanel';
import { usePlayground } from '../../shared/context/PlaygroundContext';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { preprocessingLatex } from './utils';

// Types
type PlaceholderVariant = 'pdf' | 'image' | 'file' | 'textCsv' | 'textTxt' | 'textMd' | 'docx' | 'xlsx';

// Utility functions
const getPlaceholderVariant = (filename: string): PlaceholderVariant => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  if (ext === 'csv') return 'textCsv';
  if (ext === 'txt') return 'textTxt';
  if (ext === 'md') return 'textMd';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx') return 'xlsx';
  return 'file';
};

const isPdfFile = (filename: string) => filename.toLowerCase().endsWith('.pdf');
const isImageFile = (filename: string) => {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
};

const shouldShowPageCount = (filename: string, pageCount?: number) =>
  isPdfFile(filename) && typeof pageCount === 'number' && pageCount > 0;

const formatPageCountLabel = (count: number) =>
  count === 1 ? '1 página' : `${count} páginas`;

const placeholderTypeLabels: Record<PlaceholderVariant, string> = {
  pdf: 'PDF',
  image: 'Imagem',
  file: 'Arquivo',
  textCsv: 'CSV',
  textTxt: 'Texto',
  textMd: 'Markdown',
  docx: 'Word',
  xlsx: 'Excel',
};

const getPlaceholderColors = (theme: any, variant: PlaceholderVariant) => {
  const colorMap: Record<PlaceholderVariant, { cardBg: string; border: string; iconBg: string; iconColor: string; nameColor: string; typeColor: string }> = {
    pdf: {
      cardBg: theme.file_pdf_card_bg,
      border: theme.file_pdf_border,
      iconBg: theme.file_pdf_icon_bg,
      iconColor: theme.file_pdf_icon_color,
      nameColor: theme.file_pdf_name_color,
      typeColor: theme.file_pdf_type_color,
    },
    image: {
      cardBg: theme.file_image_card_bg,
      border: theme.file_image_border,
      iconBg: theme.file_image_icon_bg,
      iconColor: theme.file_image_icon_color,
      nameColor: theme.file_image_name_color,
      typeColor: theme.file_image_type_color,
    },
    textCsv: {
      cardBg: theme.file_csv_card_bg,
      border: theme.file_csv_border,
      iconBg: theme.file_csv_icon_bg,
      iconColor: theme.file_csv_icon_color,
      nameColor: theme.file_csv_name_color,
      typeColor: theme.file_csv_type_color,
    },
    textTxt: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    textMd: {
      cardBg: theme.file_txt_card_bg,
      border: theme.file_txt_border,
      iconBg: theme.file_txt_icon_bg,
      iconColor: theme.file_txt_icon_color,
      nameColor: theme.file_txt_name_color,
      typeColor: theme.file_txt_type_color,
    },
    docx: {
      cardBg: theme.file_docx_card_bg,
      border: theme.file_docx_border,
      iconBg: theme.file_docx_icon_bg,
      iconColor: theme.file_docx_icon_color,
      nameColor: theme.file_docx_name_color,
      typeColor: theme.file_docx_type_color,
    },
    xlsx: {
      cardBg: theme.file_xlsx_card_bg,
      border: theme.file_xlsx_border,
      iconBg: theme.file_xlsx_icon_bg,
      iconColor: theme.file_xlsx_icon_color,
      nameColor: theme.file_xlsx_name_color,
      typeColor: theme.file_xlsx_type_color,
    },
    file: {
      cardBg: theme.file_generic_card_bg,
      border: theme.file_generic_border,
      iconBg: theme.file_generic_icon_bg,
      iconColor: theme.file_generic_icon_color,
      nameColor: theme.file_generic_name_color,
      typeColor: theme.file_generic_type_color,
    },
  };
  return colorMap[variant];
};

const getVariantIcon = (variant: PlaceholderVariant) => {
  switch (variant) {
    case 'pdf': return <PdfPlaceholderIcon />;
    case 'image': return <ImagePlaceholderIcon />;
    case 'textCsv':
    case 'textTxt':
    case 'textMd': return <TextPlaceholderIcon />;
    default: return <GenericPlaceholderIcon />;
  }
};

// Custom Tooltip
const CustomTooltip = styled(({ className, ...props }: any) => (
  <TooltipBase {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    fontSize: '1rem',
  },
});

// Styled Components
const UserMessageStrip = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  background-color: transparent;
  padding: 0px;
  margin: 0px;
  position: relative;
`;

const UserMessageStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  .user-message-actions {
    opacity: 1;
    pointer-events: auto;
  }
  @media (max-width: 900px) {
    .user-message-actions {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const UserMessageStripContentArea = styled.div`
  color: ${({theme})=> theme.font_color};
  width: auto;
  max-width: 100%;
  margin: 0px;
  justify-content: flex-end;
  background-color: ${({theme})=> theme.user_message_background};
  border-radius: 20px;
  white-space: normal;
  overflow-wrap: break-word;
  @media (max-width: 900px) {
    margin-right: 10px;
    max-width: 100%;
    white-space: normal;
  }
`;

const MessageStripTextArea = styled.div`
  display: flex;
  flex-direction: column;
  font-align: left;
  font-size: 1rem;
  line-height: 1.8rem;
  padding: 10px;
  max-width: 100%;
  overflow-wrap: break-word;
  font-family: 'Sora', 'Lexend', sans-serif;
  @media (max-width: 600px) {
    max-width: 100%;
  }
  & p { white-space: pre-line; }
`;

const UserMessageStripTextArea = styled(MessageStripTextArea)`
  max-width: 100%;
  & p {
    white-space: pre-wrap;
  }
`;

const UserMessageMarkdown = styled(ReactMarkdown)`
  white-space: pre-wrap;
  & p { white-space: pre-wrap; margin: 0; }
  & .katex { font-size: 1.05em; }
  & .katex-display { text-align: center; margin: 8px 0; }
  & code { background: rgba(0,0,0,0.06); padding: 1px 4px; border-radius: 3px; font-size: 0.9em; }
`;

const EditTextArea = styled.textarea`
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  background-color: ${({theme})=> theme.user_message_background};
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: 0;
  margin: 0;
  overflow: hidden;
  white-space: pre-wrap;
`;

const EditButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const EditButton = styled.button`
  padding: 0.5rem 1.3rem;
  border-radius: 20px;
  border: 1px solid ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : '#94a3b8'};
  background-color: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: 'Noka', sans-serif;
  color: ${({theme}) => theme.font_color};
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background-color: ${({theme}) => theme.hover_overlay_light};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[type="submit"] {
    border-color: ${({theme}) => theme.blue_color};
    color: ${({theme}) => theme.blue_color};
  }

  &[type="submit"]:hover:not(:disabled) {
    background-color: ${({theme}) => theme.hover_overlay_light};
    border-color: ${({theme}) => theme.blue_color_hover};
    color: ${({theme}) => theme.blue_color_hover};
  }

  &[type="button"] {
    color: ${({theme}) => theme.font_color};
    border-color: ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.35)' : '#94a3b8'};

    &:hover:not(:disabled) {
      border-color: ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'};
    }
  }
`;

const UserMessageActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  align-self: flex-end;
  margin-top: 10px;
  margin-right: 4px;
`;

const EditButtonIcon = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border: none;
  padding: 5px;
  margin-top: 0;
  line-height: 1;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const CopyButtonIcon = styled(EditButtonIcon)``;

const StyledEditIcon = styled(EditIcon)`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.action_icon_color};
  transition: color .15s ease;
`;

const StyledCopyIcon = styled(ContentCopyIcon)`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.action_icon_color};
  transition: color .15s ease;
`;

const StyledCheckIcon = styled(CheckCircleRoundedIcon)`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.action_icon_color};
`;

const OcrActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: transparent;
  border: none;
  font-size: 0.8rem;
  font-weight: 500;
  color: ${({ theme }) => theme.action_icon_color};
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.font_color};
    transform: translateX(2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const OcrActionIcon = styled(SearchIcon)`
  font-size: 1rem;
  color: inherit;
`;

// File attachment styled components
const FileContainer = styled.span`
  display: flex;
  background-color: transparent;
  border-radius: 15px;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  margin-right: 0;
  width: var(--thumb-size, 160px);
  flex: 0 0 var(--thumb-size, 160px);
`;

const FileThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const HistoryFilesContainer = styled.div<{ fileCount: number; $isMobile?: boolean; $stacked: boolean }>`
  --thumb-size: ${({ $isMobile }) => ($isMobile ? '140px' : '200px')};
  --thumb-height: ${({ $isMobile }) => ($isMobile ? '190px' : '270px')};
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'grid')};
  grid-template-columns: ${({ $stacked, fileCount }) => {
    if ($stacked) {
      return 'none';
    }
    const columns = Math.min(Math.max(fileCount, 1), 2);
    return `repeat(${columns}, var(--thumb-size))`;
  }};
  grid-auto-rows: ${({ $stacked }) => ($stacked ? 'auto' : 'var(--thumb-height)')};
  gap: ${({ $stacked }) => ($stacked ? '8px' : '12px')};
  justify-content: flex-start;
  align-items: flex-start;
  justify-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'start')};
  margin: 6px 0 0;
  width: 100%;
  max-width: ${({ $stacked, $isMobile }) => {
    if ($stacked) {
      return '100%';
    }
    if ($isMobile) {
      return '100%';
    }
    return 'calc(2 * var(--thumb-size) + 12px)';
  }};
  overflow: ${({ $stacked }) => ($stacked ? 'hidden auto' : 'visible')};

  ${({ $stacked }) =>
    $stacked && `
      flex-direction: column;
      max-height: 360px;
    `}
`;

const MessageFileContainer = styled(FileContainer)<{ $stacked: boolean }>`
  background-color: transparent;
  display: ${({ $stacked }) => ($stacked ? 'flex' : 'block')};
  justify-content: ${({ $stacked }) => ($stacked ? 'flex-start' : 'center')};
  align-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'center')};
  margin-top: 6px;
  width: ${({ $stacked }) => ($stacked ? '100%' : 'var(--thumb-size, 160px)')};
  max-width: ${({ $stacked }) => ($stacked ? '100%' : 'var(--thumb-size, 160px)')};
  flex: ${({ $stacked }) => ($stacked ? '1 1 auto' : '0 0 auto')};
  margin-right: 0;
`;

const MessageFileRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  background: transparent;
  transition: background 0.12s ease, transform 0.08s ease;
  width: 100%;
  height: 100%;

  ${({ $clickable, theme }) => $clickable && `
    &:hover {
      background: ${theme.hover_overlay_light};
      transform: translateY(-1px);
    }
  `}
`;

const MessageFileName = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.font_color};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1 1 auto;
`;

const MessageFileThumb = styled(FileThumb)`
  width: var(--thumb-size, 200px);
  height: var(--thumb-height, var(--thumb-size, 200px));
  max-width: 100%;
  object-fit: cover;
`;

const MessageFilePlaceholder = styled.div<{ $variant: PlaceholderVariant }>`
  width: var(--thumb-size, 200px);
  height: var(--thumb-height, var(--thumb-size, 200px));
  max-width: 100%;
  border-radius: 16px;
  border: 1px solid ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).border};
  background: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).cardBg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 24px 20px;
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).nameColor};
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 12px 26px rgba(0, 0, 0, 0.35)'
    : '0 12px 26px rgba(15, 23, 42, 0.08)'};
`;

const PlaceholderBadge = styled.div<{ $variant: PlaceholderVariant }>`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).iconBg};
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).iconColor};
`;

const PlaceholderDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const PlaceholderFilename = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  max-width: calc(var(--thumb-size, 200px) - 32px);
  word-break: break-word;
  white-space: normal;
`;

const PlaceholderFileType = styled.span<{ $variant: PlaceholderVariant }>`
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $variant, theme }) => getPlaceholderColors(theme, $variant).typeColor};
`;

const ThumbWrapper = styled.div`
  position: relative;
  display: inline-flex;
  width: var(--thumb-size, 160px);
  height: var(--thumb-height, var(--thumb-size, 160px));
  align-items: center;
  justify-content: center;
`;

const HistoryThumbInner = styled.div<{ $clickable: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: stretch;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  outline: none;

  ${({ $clickable, theme }) => $clickable && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${theme.mode === 'dark'
        ? '0 18px 34px rgba(0, 0, 0, 0.45)'
        : '0 14px 28px rgba(15, 23, 42, 0.12)'};
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.45);
    }
  `}
`;

const PageCountBadge = styled.div`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  font-size: 0.75rem;
  line-height: 1;
  color: white;
  background: ${({ theme }) => theme.file_page_badge_bg};
  border: 1px solid ${({ theme }) => theme.file_page_badge_border};
  border-radius: 999px;
  pointer-events: none;
`;

const CondensedAttachmentIcon = styled.div<{ $variant: PlaceholderVariant }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconBg};
  color: ${({ theme, $variant }) => getPlaceholderColors(theme, $variant).iconColor};
`;

// Bot message styled components
const BotMessageStrip = styled.div`
  display: flex;
  width: 100%;
  max-width: 100%;
  justify-content: flex-start;
  padding: 10px;
  position: relative;

  &.copy-always-visible .assistant-action-button {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
  .assistant-action-button {
    opacity: 1;
    pointer-events: auto;
  }
  @media (max-width: 600px) {
    .assistant-action-button {
      opacity: 1 !important;
      pointer-events: auto !important;
    }
  }
`;

const MessageStripContentArea = styled.div`
  display: flex;
  justify-content: flex-start;
  color: ${({theme})=> theme.font_color};
  width: 100%;
  max-width: 100%;
  margin: 0px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  &.copy-always-visible .assistant-action-button {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
  &:hover .assistant-action-button {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
`;

const ActionIconButton = styled(IconButton)`
  && { color: ${({ theme }) => theme.action_icon_color}; }
`;

const MoreMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const MoreMenu = styled.div`
  position: absolute;
  z-index: 1200;
  bottom: calc(100% + 4px);
  left: 0;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.user_menu_border};
  background-color: ${({ theme }) => theme.sidebar_background_solid};
  min-width: 180px;
  white-space: nowrap;
  overflow: visible;
  box-shadow: ${({ theme }) => theme.mode === 'dark'
    ? '0 4px 12px rgba(0,0,0,0.4)'
    : '0 4px 12px rgba(0,0,0,0.1)'};
`;

const MoreMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
`;

const MoreMenuIcon = styled.span`
  display: flex;
  align-items: center;
  opacity: 0.6;
  font-size: 1.1rem;
`;

const MobileBottomSpacer = styled.div`
  display: none;
  @media (max-width: 1024px) {
    display: block;
    height: 80px;
    flex-shrink: 0;
  }
`;

const MoreMenuComponentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-family: 'Sora', 'Lexend', sans-serif;
  color: ${({ theme }) => theme.font_color};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
  }
  & .assistant-action-button {
    opacity: 1 !important;
    pointer-events: auto !important;
    width: auto;
    height: auto;
    padding: 0;
    min-width: 0;
  }
  & .assistant-action-button svg {
    font-size: 1.1rem;
    opacity: 0.6;
  }
`;

const EndOfChatMessageMarker = styled.div`
  opacity: 0;
  height: 200px;
`;

// Placeholder icons
const PdfPlaceholderIcon = MUIStyled(PictureAsPdfIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const ImagePlaceholderIcon = MUIStyled(InsertPhotoIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const GenericPlaceholderIcon = MUIStyled(InsertDriveFileIcon)`
  font-size: 2rem;
  color: currentColor;
`;

const TextPlaceholderIcon = MUIStyled(ArticleIcon)`
  font-size: 2rem;
  color: currentColor;
`;

// --- Download response utilities ---

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadAsMarkdown(content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  downloadBlob(blob, 'resposta.md');
}

function markdownToSimpleHtml(md: string): string {
  let html = md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/^[-*_]{3,}$/gm, '<hr>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\*(.+?)\*/g, '<i>$1</i>')
    .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:2px 4px;border-radius:3px;font-family:monospace">$1</code>')
    .replace(/^\- (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>');
  // Convert remaining lines to paragraphs
  html = html.split('\n').map(line => {
    if (line.startsWith('<h') || line.startsWith('<ul') || line.startsWith('<li') || line.startsWith('<hr') || line.trim() === '') return line;
    return `<p>${line}</p>`;
  }).join('\n');

  return html;
}

/**
 * Clones the rendered message DOM, converts <canvas> (Chart.js) to <img>,
 * and strips action buttons / ref buttons so only content remains.
 */
function cloneRenderedContent(buttonEl: HTMLElement): string {
  // Walk up to the MessageStripTextArea container
  const messageArea = buttonEl.closest('[data-message-content]');
  if (!messageArea) return '';

  const clone = messageArea.cloneNode(true) as HTMLElement;

  // Convert all <canvas> to <img> (Chart.js charts)
  const originalCanvases = messageArea.querySelectorAll('canvas');
  const clonedCanvases = clone.querySelectorAll('canvas');
  clonedCanvases.forEach((clonedCanvas, i) => {
    const originalCanvas = originalCanvases[i] as HTMLCanvasElement;
    if (originalCanvas) {
      const img = document.createElement('img');
      try {
        img.src = originalCanvas.toDataURL('image/png');
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      } catch {
        // canvas tainted — skip
      }
      clonedCanvas.parentNode?.replaceChild(img, clonedCanvas);
    }
  });

  // Remove action buttons, ref buttons, toolbars, and standalone buttons
  // Preserve SVGs inside .katex elements (math equations)
  clone.querySelectorAll('[data-download-exclude], button').forEach(el => el.remove());
  clone.querySelectorAll('svg').forEach(el => {
    if (!el.closest('.katex')) el.remove();
  });

  return clone.innerHTML;
}


function extractChartImages(buttonEl: HTMLElement): string[] {
  const images: string[] = [];
  const messageArea = buttonEl.closest('[data-message-content]');
  if (!messageArea) return images;
  messageArea.querySelectorAll('canvas').forEach((canvas) => {
    try {
      images.push((canvas as HTMLCanvasElement).toDataURL('image/png'));
    } catch { /* canvas tainted */ }
  });
  return images;
}

async function downloadViaBackend(buttonEl: HTMLElement, content: string, format: 'pdf' | 'docx', getToken: () => Promise<string>) {
  const renderedHtml = cloneRenderedContent(buttonEl);
  const bodyHtml = renderedHtml || markdownToSimpleHtml(content);
  const chartImages = extractChartImages(buttonEl);
  const token = await getToken();
  const ext = format === 'pdf' ? 'pdf' : 'docx';

  const res = await fetch('/api/chat/export', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Auth ${token}`,
    },
    body: JSON.stringify({ html: bodyHtml, markdown: content, chart_images: chartImages, format }),
  });

  if (!res.ok) {
    // Fallback to markdown download on error
    downloadAsMarkdown(content);
    return;
  }

  const blob = await res.blob();
  saveAs(blob, `resposta.${ext}`);
}

const DownloadResponseButton: React.FC<{ content: string }> = ({ content }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const getButtonEl = () => wrapperRef.current as HTMLElement;

  const handleBackendDownload = async (format: 'pdf' | 'docx') => {
    setOpen(false);
    setLoading(true);
    try {
      await downloadViaBackend(getButtonEl(), content, format, getAccessTokenSilently);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DownloadButtonWrapper ref={wrapperRef}>
      <CustomTooltip placement="top" title={open || loading ? '' : 'Baixar resposta'}>
        <ActionIconButton
          className="assistant-action-button"
          onClick={() => { if (!loading) setOpen(prev => !prev); }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <FileDownloadOutlinedIcon />}
        </ActionIconButton>
      </CustomTooltip>
      {open && (
        <DownloadDropdown>
          <DownloadDropdownItem onClick={() => { downloadAsMarkdown(content); setOpen(false); }}>
            Markdown (.md)
          </DownloadDropdownItem>
          <DownloadDropdownItem onClick={() => handleBackendDownload('docx')}>
            Word (.docx)
          </DownloadDropdownItem>
          <DownloadDropdownItem onClick={() => handleBackendDownload('pdf')}>
            PDF
          </DownloadDropdownItem>
        </DownloadDropdown>
      )}
    </DownloadButtonWrapper>
  );
};

const DownloadButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const DownloadDropdown = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  margin-left: 4px;
  background: ${({ theme }) => theme.chart_bg || theme.base_color};
  border: 1px solid ${({ theme }) => theme.chart_border || theme.borderGrey};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  z-index: 20;
  overflow: hidden;
  min-width: 140px;
`;

const DownloadDropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 0.8125rem;
  white-space: nowrap;
  color: ${({ theme }) => theme.font_color};
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.chart_border || theme.borderGrey};
  }
`;

const canStreamAudio = typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

type TtsState = 'idle' | 'loading' | 'playing' | 'paused' | 'ended';

// Module-level: only one TTS instance plays at a time.
// When a new instance starts, it calls this to stop the previous one.
let activeStopFn: (() => void) | null = null;

/** Stop any TTS playback globally. Call when switching conversations. */
export const stopAllTts = () => {
  if (activeStopFn) {
    activeStopFn();
    activeStopFn = null;
  }
};

const TextToSpeechButton: React.FC<{ content: string; className?: string }> = ({ content, className }) => {
  const [ttsState, setTtsState] = useState<TtsState>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const prevContentRef = useRef(content);

  // Reset TTS state when content changes (e.g. switching conversations)
  useEffect(() => {
    if (prevContentRef.current !== content) {
      prevContentRef.current = content;
      if (audioRef.current) {
        const src = audioRef.current.src;
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
        if (src) URL.revokeObjectURL(src);
      }
      mediaSourceRef.current = null;
      setTtsState('idle');
    }
  }, [content]);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      mediaSourceRef.current = null;
    };
  }, []);

  const stop = () => {
    if (activeStopFn === stop) activeStopFn = null;
    if (audioRef.current) {
      const src = audioRef.current.src;
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
      if (src) URL.revokeObjectURL(src);
    }
    mediaSourceRef.current = null;
    setTtsState('idle');
  };

  const onEnded = () => setTtsState('ended');
  const onError = () => stop();

  const setupAudio = (audio: HTMLAudioElement) => {
    audioRef.current = audio;
    audio.onended = onEnded;
    audio.onerror = onError;
  };

  const playStreaming = async (res: Response) => {
    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    const url = URL.createObjectURL(mediaSource);
    const audio = new Audio(url);
    setupAudio(audio);

    await new Promise<void>((resolve, reject) => {
      mediaSource.addEventListener('sourceopen', async () => {
        try {
          const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          const reader = res.body!.getReader();
          let started = false;
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              if (sourceBuffer.updating) {
                await new Promise<void>(r => sourceBuffer.addEventListener('updateend', () => r(), { once: true }));
              }
              if (mediaSource.readyState === 'open') mediaSource.endOfStream();
              break;
            }
            if (sourceBuffer.updating) {
              await new Promise<void>(r => sourceBuffer.addEventListener('updateend', () => r(), { once: true }));
            }
            sourceBuffer.appendBuffer(value);
            if (!started) {
              started = true;
              audio.play().then(() => setTtsState('playing')).catch(() => {});
            }
          }
          resolve();
        } catch (e) {
          reject(e);
        }
      }, { once: true });
    });
  };

  const playBlob = async (res: Response) => {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    setupAudio(audio);
    await audio.play();
    setTtsState('playing');
  };

  const fetchAndPlay = async () => {
    // Stop any other TTS instance that's currently playing
    if (activeStopFn) activeStopFn();
    activeStopFn = stop;
    setTtsState('loading');
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch('/api/tts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Auth ${token}`,
        },
        body: JSON.stringify({ text: content.slice(0, 5000) }),
      });
      if (!res.ok) throw new Error('TTS request failed');

      if (canStreamAudio && res.body) {
        await playStreaming(res);
      } else {
        await playBlob(res);
      }
    } catch {
      stop();
    }
  };

  const pause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setTtsState('paused');
    }
  };

  const resume = () => {
    if (audioRef.current) {
      activeStopFn = stop;
      audioRef.current.play().then(
        () => setTtsState('playing'),
        () => stop()
      );
    }
  };

  const replay = () => {
    if (audioRef.current) {
      activeStopFn = stop;
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(
        () => setTtsState('playing'),
        () => stop()
      );
    }
  };

  if (ttsState === 'idle') {
    return (
      <CustomTooltip placement="top" title="Ouvir mensagem">
        <ActionIconButton className={className} onClick={fetchAndPlay}>
          <VolumeUpIcon />
        </ActionIconButton>
      </CustomTooltip>
    );
  }

  if (ttsState === 'loading') {
    return (
      <ActionIconButton className={className} disabled>
        <CircularProgress size={18} sx={{ color: 'inherit' }} />
      </ActionIconButton>
    );
  }

  if (ttsState === 'playing') {
    return (
      <>
        <CustomTooltip placement="top" title="Pausar">
          <ActionIconButton className={className} onClick={pause}>
            <PauseIcon />
          </ActionIconButton>
        </CustomTooltip>
        <CustomTooltip placement="top" title="Parar">
          <ActionIconButton className={className} onClick={stop}>
            <StopIcon />
          </ActionIconButton>
        </CustomTooltip>
      </>
    );
  }

  if (ttsState === 'paused') {
    return (
      <>
        <CustomTooltip placement="top" title="Continuar">
          <ActionIconButton className={className} onClick={resume}>
            <PlayArrowIcon />
          </ActionIconButton>
        </CustomTooltip>
        <CustomTooltip placement="top" title="Parar">
          <ActionIconButton className={className} onClick={stop}>
            <StopIcon />
          </ActionIconButton>
        </CustomTooltip>
      </>
    );
  }

  // ttsState === 'ended'
  return (
    <CustomTooltip placement="top" title="Ouvir novamente">
      <ActionIconButton className={className} onClick={replay}>
        <ReplayIcon />
      </ActionIconButton>
    </CustomTooltip>
  );
};

const getTextContent = (content: string | ContentItem[]): string => {
  if (typeof content === 'string') {
    return content;
  }
  return content
    .filter((item): item is TextContentItem => item.type === 'text')
    .map(item => item.text)
    .join('\n');
};

// Helper to extract file items from array content
const getFileItems = (content: string | ContentItem[]): FileContentItem[] => {
  if (typeof content === 'string') {
    return [];
  }
  return content.filter((item): item is FileContentItem => item.type === 'input_file');
};

// Helper to extract file_ref items from array content (new format)
const getFileRefItems = (content: string | ContentItem[]): FileRefContentItem[] => {
  if (typeof content === 'string') {
    return [];
  }
  return content.filter((item): item is FileRefContentItem => item.type === 'file_ref');
};

// Collapsible "Raciocínio" (thinking) block — shows the reasoning the model
// wrote in the no-op reasoning tool. Collapsed by default.
const ReasoningRow = styled.div`
  display: flex; flex-direction: column; margin: 4px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;
const ReasoningHeader = styled.div`
  display: inline-flex; align-items: center; gap: 6px; align-self: flex-start;
  padding: 4px 8px 4px 0; cursor: pointer; user-select: none; border-radius: 6px;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  font-size: 0.88rem; font-weight: 500;
  &:hover { background: ${({ theme }) => theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}; }
`;
const ReasoningBody = styled.div`
  margin: 2px 0 8px 22px; white-space: pre-wrap; word-break: break-word;
  font-size: 0.85rem; line-height: 1.5;
  color: ${({ theme }) => theme.text_secondary || theme.font_color}; opacity: 0.85;
`;
// When REACT_APP_HIDE_THINKING is "true" (set at build time, e.g. in prod), the
// model's reasoning is NEVER shown to end users — the collapsible "Pensamento"
// block is replaced by a static, non-expandable "Pensando..." marker. Leave the
// flag unset in dev/debug builds to inspect the full reasoning.
const HIDE_THINKING = process.env.REACT_APP_HIDE_THINKING === 'true';

const ReasoningBlock: React.FC<{ reasoning: string }> = ({ reasoning }) => {
  const [open, setOpen] = useState(false);
  if (!reasoning || !reasoning.trim()) return null;
  if (HIDE_THINKING) {
    return (
      <ReasoningRow>
        <ReasoningHeader as="div" style={{ cursor: 'default' }}>
          <PsychologyIcon style={{ fontSize: 15, opacity: 0.85 }} />
          Pensando...
        </ReasoningHeader>
      </ReasoningRow>
    );
  }
  return (
    <ReasoningRow>
      <ReasoningHeader onClick={() => setOpen(v => !v)}>
        <PsychologyIcon style={{ fontSize: 15, opacity: 0.85, marginLeft: -3 }} />
        Pensamento
        {open ? <ExpandLessIcon style={{ fontSize: 16, opacity: 0.6 }} /> : <ExpandMoreIcon style={{ fontSize: 16, opacity: 0.6 }} />}
      </ReasoningHeader>
      {open && <ReasoningBody>{reasoning}</ReasoningBody>}
    </ReasoningRow>
  );
};

// Renders a ContentItem[] (or string fallback) in generation order: text, then file, then text...
const renderContentInOrder = (
  content: string | ContentItem[] | undefined,
  references: Reference[],
  formatAssistantMessage: (text: string, refs: Reference[], isStreaming?: boolean) => React.ReactNode,
  isStreaming?: boolean,
  hideToolItems?: boolean,
  messageInterrupted?: boolean,
): React.ReactNode => {
  if (!content) return null;
  if (typeof content === 'string') {
    return content ? formatAssistantMessage(content, references, isStreaming) : null;
  }
  // Collapse every consecutive burst of reasoning/tool/code items — anywhere
  // in the message, not just before the answer — into ONE expandable
  // "Pensando…/Pensou" line: a pile of boxes between text segments reads as
  // noise. A single bare tool call stays inline, and visible answer text is
  // never absorbed. Deep research (hideToolItems) keeps its own layout.
  const thinkingRuns = hideToolItems ? [] : computeThinkingRuns(content);
  const runByStart = new Map(thinkingRuns.map((r) => [r.startIndex, r]));
  const swallowedIdx = groupedIndexSet(thinkingRuns, content);
  return (
    <>
      {/* Render every item in chronological order — including reasoning blocks
          in-place. This conveys SEQUENCE: text → [tool/reasoning box] → text →
          [new box]. (Reasoning is no longer hoisted to the top; a reasoning or
          tool burst that happens AFTER the model already wrote text shows in its
          own box below that text, matching how it streamed.) */}
      {content.map((item, i) => {
        const el = (() => {
        const thinkingRun = runByStart.get(i);
        if (thinkingRun) {
          return (
            <ThinkingGroup
              key={`think-${i}`}
              items={thinkingRun.items}
              pending={
                thinkingBurstPending(thinkingRun.items) ||
                (!!isStreaming && !hasVisibleItemsAfter(content, thinkingRun.endIndex))
              }
              interrupted={
                // Only the run that was live at the stop: nothing visible after it.
                !!messageInterrupted && !hasVisibleItemsAfter(content, thinkingRun.endIndex)
              }
            />
          );
        }
        if (swallowedIdx.has(i)) {
          return null; // rendered inside its ThinkingGroup above
        }
        if (hideToolItems && (item.type === 'tool_action' || item.type === 'code_execution' || item.type === 'search_results')) {
          return null;
        }
        if (item.type === 'text' && item.text) {
          return (
            <React.Fragment key={i}>
              {formatAssistantMessage(item.text, references, isStreaming)}
            </React.Fragment>
          );
        }
        if (item.type === 'file_ref') {
          return (
            <GeneratedFilePreview key={i} fileRefs={[{
              file_id: item.file_id,
              title: item.title,
              file_type: item.file_type as FileRef['file_type'],
              mime_type: item.mime_type,
              editable: item.editable,
              blob_size_bytes: item.blob_size_bytes || 0,
            }]} />
          );
        }
        if (item.type === 'input_file') {
          return (
            <GeneratedFilePreview key={i} files={[{
              path: item.filename,
              mime_type: item.mime_type || 'application/octet-stream',
              size_bytes: item.size_bytes || 0,
              content_base64: item.file_data,
              ...(item.content ? { content: item.content } : {}),
            }]} />
          );
        }
        if (item.type === 'message_composer') {
          return <MessageComposerPreview key={i} data={item} />;
        }
        if (item.type === 'panorama_confirmation') {
          return <PanoramaConfirmation key={i} data={item} />;
        }
        if (item.type === 'panorama_debug') {
          return <PanoramaDebug key={i} data={item} />;
        }
        if (item.type === 'panorama_summary') {
          return <PanoramaProgress key={i} phase="done" progress={1} nSearches={(item as any).nSearches} nDocs={(item as any).nDocs} nTheses={(item as any).nTheses} inputTokens={(item as any).inputTokens} outputTokens={(item as any).outputTokens} />;
        }
        if (item.type === 'reasoning') {
          return <ReasoningBlock key={i} reasoning={(item as any).reasoning || ''} />;
        }
        if (item.type === 'tool_action') {
          return <ToolActionBlock key={i} data={item as ToolActionContentItem} />;
        }
        if (item.type === 'code_execution') {
          return <CodeExecutionBlock key={i} data={item as CodeExecutionContentItem} />;
        }
        if (item.type === 'search_results') {
          return <SearchResultsBlock key={i} data={item as SearchResultsContentItem} />;
        }
        return null;
        })();
        if (el == null) return null;
        return (
          <ContentItemErrorBoundary key={i} itemType={(item as any)?.type} resetKeys={[!!isStreaming]}>
            {el}
          </ContentItemErrorBoundary>
        );
      })}
    </>
  );
};

const MessageList: React.FC = () => {
  const {
    isMobile,
    messageHistory,
    messageBeingGenerated,
    loadingGeneration,
    loadingMessage,
    extractingFiles,
    chatErrorMessage,
    allowErrorRetry,
    showPlanUpgradeOption,
    isEditing,
    editingMessageId,
    messageBeingEdited,
    copiedIndex,
    planStatus,
    freeQuota,
    quotaBadgeIndex,
    filePreviews,
    fileTextPreviews,
    numPages,
    fileDownloadProgress,
    loadingOcrModal,
    activeChatID,
    handleEditMessage,
    handleSaveEditedMessage,
    handleCancelEdit,
    handleEditMessageInputChange,
    handleCopyToClipboard,
    handleLikeMessage,
    handleDislikeClick,
    handleRegenerateMessage,
    handleRetryMessage,
    handleOpenPreview,
    handleOpenOcrModal,
    hasOcrContentForMessage,
    setOpenSubscriptionModal,
    formatAssistantMessage,
    getVisibleReferences,
    getCitedReferencesWithHistory,
    renderQuotaInfoTrigger,
    endOfChatMessageMarkerRef,
    deepResearchPhase,
    deepResearchProgress,
    deepResearchPlan,
    deepResearchSteps,
    deepResearchTokens,
    panoramaPhase,
    panoramaProgress,
    panoramaNSearches,
    panoramaNDocs,
    panoramaNTheses,
    panoramaInputTokens,
    panoramaOutputTokens,
    debugMode,
    currentToolCalls,
  } = usePlayground();

  const [moreMenuIndex, setMoreMenuIndex] = useState<number | null>(null);
  useEffect(() => {
    if (moreMenuIndex === null) return;
    const handler = () => setMoreMenuIndex(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [moreMenuIndex]);

  // Per-message share state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareMessages, setShareMessages] = useState<{role: 'user' | 'assistant'; content: string | ContentItem[]}[] | undefined>(undefined);
  const [shareMessageIndex, setShareMessageIndex] = useState<number | undefined>(undefined);

  const handleShareSingleMessage = (assistantIndex: number) => {
    const assistantMsg = messageHistory[assistantIndex];
    // Preserve full content (including file_ref items for charts/images)
    const msgs: {role: 'user' | 'assistant'; content: string | ContentItem[]; references?: any[]}[] = [
      { role: 'assistant', content: assistantMsg.content, references: (assistantMsg as any).references }
    ];
    setShareMessages(msgs);
    setShareMessageIndex(assistantIndex);
    setShareModalOpen(true);
  };

  return (
    <>
      {messageHistory.map((message: any, index) => {
        if (message.role === "user") {
          const isLastOrSecondToLastMessage = index === messageHistory.length - 1 ? index === messageHistory.length - 1 : index === messageHistory.length - 2;

          return (
            <UserMessageStrip key={index}>
              <UserMessageStack>
                <UserMessageStripContentArea
                  style={{ width: isEditing && isLastOrSecondToLastMessage ? "100%" : "auto", maxWidth: isEditing && isLastOrSecondToLastMessage ? "100%" : "80%" }}
                >
                  <UserMessageStripTextArea>
                    {isEditing && editingMessageId === index ? (
                      <form onSubmit={handleSaveEditedMessage}>
                        <EditTextArea
                          id={`edit-textarea-${index}`}
                          value={messageBeingEdited}
                          onChange={handleEditMessageInputChange}
                        />
                        <EditButtons>
                          <EditButton type="submit" disabled={(loadingGeneration) || messageBeingEdited.trim().length == 0}>Enviar</EditButton>
                          <EditButton type="button" onClick={handleCancelEdit}>Cancelar</EditButton>
                        </EditButtons>
                      </form>
                    ) : (
                      <UserMessageMarkdown
                        remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
                        rehypePlugins={[rehypeKatex]}
                        components={{ p: ({ children }) => <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{children}</p> }}
                      >
                        {preprocessingLatex(message.content)}
                      </UserMessageMarkdown>
                    )}
                    {!isEditing && message.filenames && message.filenames.length > 0 && (
                      (() => {
                        const fileCountForLayout = message.filenames.length;
                        const shouldStackAttachments = fileCountForLayout > 6;
                        const showGridPreviews = !shouldStackAttachments;

                        return (
                          <HistoryFilesContainer
                            fileCount={fileCountForLayout}
                            $isMobile={isMobile}
                            $stacked={shouldStackAttachments}
                          >
                            {message.filenames.map((filename: string, fileIdx: number) => {
                              const variant = getPlaceholderVariant(filename);
                              const pageCount = numPages?.[filename];
                              const previewUrl = filePreviews[filename];
                              const _lowerFn = filename.toLowerCase();
                              const _isOfficeFile = _lowerFn.endsWith('.docx') || _lowerFn.endsWith('.xlsx') || _lowerFn.endsWith('.rtf');
                              const canPreview = Boolean(previewUrl) || Boolean(fileTextPreviews[filename]) || isPdfFile(filename) || isImageFile(filename) || _isOfficeFile;
                              const showPageCountFlag = shouldShowPageCount(filename, pageCount);
                              const pageCountLabel = showPageCountFlag && typeof pageCount === 'number'
                                ? formatPageCountLabel(pageCount)
                                : '';

                              if (showGridPreviews) {
                                const placeholderIcon = (() => {
                                  if (variant === 'pdf') return <PdfPlaceholderIcon />;
                                  if (variant === 'image') return <ImagePlaceholderIcon />;
                                  if (variant === 'textCsv' || variant === 'textTxt') return <TextPlaceholderIcon />;
                                  return <GenericPlaceholderIcon />;
                                })();

                                const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
                                  if (!canPreview) {
                                    return;
                                  }
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleOpenPreview(filename);
                                  }
                                };

                                return (
                                  <MessageFileContainer key={`${filename}-${fileIdx}`} $stacked={false}>
                                    <CustomTooltip title={filename} placement="top">
                                      <ThumbWrapper>
                                        {(() => {
                                          const dlProgress = fileDownloadProgress[filename];
                                          const isDownloading = dlProgress !== undefined && dlProgress < 100;
                                          return isDownloading ? (
                                            <HistoryThumbInner $clickable={false}>
                                              <div style={{
                                                width: '100%', height: '100%',
                                                display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center',
                                                gap: 6,
                                              }}>
                                                <div style={{ position: 'relative', display: 'inline-flex' }}>
                                                  <CircularProgress
                                                    variant="determinate"
                                                    value={dlProgress}
                                                    size={44}
                                                    thickness={4}
                                                  />
                                                  <div style={{
                                                    position: 'absolute', inset: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  }}>
                                                    <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.8 }}>
                                                      {dlProgress}%
                                                    </span>
                                                  </div>
                                                </div>
                                                <span style={{ fontSize: 11, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '90%' }} title={filename}>
                                                  {filename}
                                                </span>
                                              </div>
                                            </HistoryThumbInner>
                                          ) : (
                                            <HistoryThumbInner
                                              $clickable={canPreview}
                                              role={canPreview ? 'button' : undefined}
                                              tabIndex={canPreview ? 0 : -1}
                                              onClick={canPreview ? () => handleOpenPreview(filename) : undefined}
                                              onKeyDown={handleKeyDown}
                                            >
                                              {previewUrl ? (
                                                <MessageFileThumb src={previewUrl} alt={filename} />
                                              ) : (
                                                <MessageFilePlaceholder $variant={variant}>
                                                  <PlaceholderBadge $variant={variant}>
                                                    {placeholderIcon}
                                                  </PlaceholderBadge>
                                                  <PlaceholderDetails>
                                                    <PlaceholderFilename title={filename}>{filename}</PlaceholderFilename>
                                                    <PlaceholderFileType $variant={variant}>
                                                      {placeholderTypeLabels[variant]}
                                                    </PlaceholderFileType>
                                                  </PlaceholderDetails>
                                                </MessageFilePlaceholder>
                                              )}
                                            </HistoryThumbInner>
                                          );
                                        })()}
                                        {pageCountLabel && (
                                          <PageCountBadge>
                                            {pageCountLabel}
                                          </PageCountBadge>
                                        )}
                                      </ThumbWrapper>
                                    </CustomTooltip>
                                  </MessageFileContainer>
                                );
                              }

                              const pageInfo = pageCountLabel
                                ? ` • ${pageCountLabel}`
                                : '';

                              const rowPreviewIcon = getVariantIcon(variant);

                              {
                                const dlProgress = fileDownloadProgress[filename];
                                const isDownloading = dlProgress !== undefined && dlProgress < 100;
                                return (
                                  <MessageFileContainer key={`${filename}-${fileIdx}`} $stacked={true}>
                                    <MessageFileRow
                                      role={canPreview && !isDownloading ? 'button' : undefined}
                                      onClick={canPreview && !isDownloading ? () => handleOpenPreview(filename) : undefined}
                                      $clickable={canPreview && !isDownloading}
                                    >
                                      {isDownloading ? (
                                        <CircularProgress
                                          variant="determinate"
                                          value={dlProgress}
                                          size={18}
                                          thickness={5}
                                          sx={{ color: 'inherit', marginRight: '8px', flexShrink: 0 }}
                                        />
                                      ) : (
                                        <CondensedAttachmentIcon $variant={variant}>
                                          {rowPreviewIcon}
                                        </CondensedAttachmentIcon>
                                      )}
                                      <MessageFileName title={filename}>
                                        {filename}{isDownloading ? ` ${dlProgress}%` : pageInfo}
                                      </MessageFileName>
                                    </MessageFileRow>
                                  </MessageFileContainer>
                                );
                              }
                            })}
                          </HistoryFilesContainer>
                        );
                      })()
                    )}
                  </UserMessageStripTextArea>
                </UserMessageStripContentArea>
                {!isEditing && (
                  <UserMessageActions className="user-message-actions">
                    <CustomTooltip
                      title={copiedIndex === index ? 'Copiado!' : 'Copiar mensagem'}
                      placement="top"
                    >
                      <CopyButtonIcon
                        type="button"
                        onClick={() => handleCopyToClipboard(message.content, index, message.references as Reference[] | undefined)}
                      >
                        {copiedIndex === index ? <StyledCheckIcon /> : <StyledCopyIcon />}
                      </CopyButtonIcon>
                    </CustomTooltip>
                    {isLastOrSecondToLastMessage && (
                      <CustomTooltip title="Editar mensagem" placement="top">
                        <EditButtonIcon onClick={() => handleEditMessage(index, message.content)}>
                          <StyledEditIcon />
                        </EditButtonIcon>
                      </CustomTooltip>
                    )}
                  </UserMessageActions>
                )}
              </UserMessageStack>
            </UserMessageStrip>
          )
        } else if (message.role === "assistant" && ((!messageBeingGenerated || index !== messageHistory.length - 1)))  {
          const isLastAssistantMessage = index === messageHistory.length - 1;
          const textContent = getTextContent(message.content);
          const fileItemsFromContent = getFileItems(message.content);
          const fileRefItemsFromContent = getFileRefItems(message.content);
          // Also count tool_action and code_execution items so the bubble
          // renders as soon as the first tool event lands (otherwise the
          // assistant looks empty for the ~1-2s between the tool firing
          // and the first text chunk arriving).
          const hasToolItems = Array.isArray(message.content)
            && message.content.some((it: any) => it && (it.type === 'tool_action' || it.type === 'code_execution'));
          // Reasoning blocks count as content too, so a reasoning-only message
          // (cycle-0 "Pensando...") renders here, in-place — and we no longer
          // need the separate pre-answer reasoning strip below (which used to
          // render the SAME reasoning a second time once a tool/text appeared).
          const hasReasoning = Array.isArray(message.content)
            && message.content.some((it: any) => it && it.type === 'reasoning');
          const hasContent = textContent !== "" || fileItemsFromContent.length > 0 || fileRefItemsFromContent.length > 0 || hasToolItems || hasReasoning;
          const hideActionButtons = isLastAssistantMessage && (loadingGeneration || !!loadingMessage || !!messageBeingGenerated);
          if (hasContent) {
            const drMeta = message.deep_research_metadata;
            const allRefs = (message.references as Reference[]) || [];
            const messageRefs = allRefs.filter(ref => ref.link && textContent.includes(ref.link));
            return (
              <BotMessageStrip key={index} className={isLastAssistantMessage ? 'copy-always-visible' : ''}>
                <MessageStripContentArea>
                  <MessageStripTextArea data-message-content>
                    {drMeta && drMeta.plan && (
                      <DeepResearchProgress
                        phase="done"
                        progress={1}
                        steps={drMeta.step_results || {}}
                        inputTokens={drMeta.input_tokens || 0}
                        outputTokens={drMeta.output_tokens || 0}
                      />
                    )}
                    {renderContentInOrder(message.content, messageRefs, formatAssistantMessage,
                      /* The reasoning phase renders via this map path (messageBeingGenerated is
                         still empty); pass the streaming flag so the trailing thinking group
                         keeps "Pensando" between the cycle-0 draft finishing and the first
                         tool starting, instead of flickering to "Pensou" mid-run. */
                      loadingGeneration && isLastAssistantMessage, !!drMeta || (isLastAssistantMessage && !!deepResearchPhase),
                      !!(message as any).interrupted)}
                    {/* Fallback: old messages stored as string + generated_files (no ContentItem[]) */}
                    {typeof message.content === 'string' && message.generated_files && message.generated_files.length > 0 && (() => {
                      const genFiles = message.generated_files as (GeneratedFile | FileRef)[];
                      return (
                        <GeneratedFilePreview
                          files={genFiles.filter((f): f is GeneratedFile => 'content_base64' in f)}
                          fileRefs={genFiles.filter((f): f is FileRef => 'file_id' in f)}
                        />
                      );
                    })()}
                    {!hideActionButtons && (
                      <ActionButtonsContainer data-download-exclude className={isLastAssistantMessage && !loadingGeneration ? 'copy-always-visible' : ''}>
                      <CustomTooltip placement="top" title={copiedIndex === index ? 'Copiado!' : 'Copiar mensagem'}>
                        <ActionIconButton className="assistant-action-button copy-icon" onClick={() => handleCopyToClipboard(textContent, index, allRefs)}>
                          {copiedIndex === index ? <CheckCircleRoundedIcon /> : <ContentCopyIcon />}
                        </ActionIconButton>
                      </CustomTooltip>
                      {isLastAssistantMessage && !loadingGeneration && (
                        <CustomTooltip placement="top" title="Gerar nova resposta">
                          <ActionIconButton className="assistant-action-button regenerate-icon" onClick={() => handleRegenerateMessage(index-1)}>
                            <ReplayIcon />
                          </ActionIconButton>
                        </CustomTooltip>
                      )}
                      <CustomTooltip placement="top" title={message.feedback === 'like' ? 'Remover gostei' : 'Gostei da resposta'}>
                        <ActionIconButton className="assistant-action-button like-icon" onClick={() => handleLikeMessage(index)}>
                          <ThumbUpOutlinedIcon sx={{ color: message.feedback === 'like' ? '#4f46e5' : 'inherit' }} />
                        </ActionIconButton>
                      </CustomTooltip>
                      <CustomTooltip placement="top" title={message.feedback === 'dislike' ? 'Remover feedback' : 'Não gostei da resposta'}>
                        <ActionIconButton className="assistant-action-button dislike-icon" onClick={(e) => handleDislikeClick(e, index)}>
                          <ThumbDownOutlinedIcon sx={{ color: message.feedback === 'dislike' ? 'error.main' : 'inherit' }} />
                        </ActionIconButton>
                      </CustomTooltip>

                      <TextToSpeechButton content={textContent} className="assistant-action-button" />
                      <MoreMenuWrapper>
                        <ActionIconButton className="assistant-action-button" onClick={(e) => { e.stopPropagation(); setMoreMenuIndex(moreMenuIndex === index ? null : index); }}>
                          <MoreHorizIcon />
                        </ActionIconButton>
                        {moreMenuIndex === index && (
                          <MoreMenu onClick={(e) => e.stopPropagation()}>
                            <MoreMenuItem onClick={() => { handleShareSingleMessage(index); setMoreMenuIndex(null); }}>
                              <MoreMenuIcon><IosShareIcon style={{ fontSize: '1.1rem' }} /></MoreMenuIcon>
                              Compartilhar
                            </MoreMenuItem>

                            <MoreMenuComponentRow onClick={(e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest('button')) { const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); } }}>
                              <DownloadResponseButton content={textContent} />
                              Baixar resposta
                            </MoreMenuComponentRow>
                          </MoreMenu>
                        )}
                      </MoreMenuWrapper>
                      </ActionButtonsContainer>
                    )}
                  </MessageStripTextArea>
                </MessageStripContentArea>
              </BotMessageStrip>
            );
          }
        }
        return null;
      })}

      {panoramaPhase && panoramaPhase !== 'done' && (
        <BotMessageStrip>
          <MessageStripContentArea>
            <MessageStripTextArea>
              <PanoramaProgress phase={panoramaPhase} progress={panoramaProgress} nSearches={panoramaNSearches} nDocs={panoramaNDocs} nTheses={panoramaNTheses} inputTokens={panoramaInputTokens} outputTokens={panoramaOutputTokens} />
            </MessageStripTextArea>
          </MessageStripContentArea>
        </BotMessageStrip>
      )}

      {(loadingGeneration || loadingMessage) && !messageBeingGenerated && !(deepResearchPhase && deepResearchPlan.length > 0) && !(panoramaPhase && panoramaPhase !== 'done') && (() => {
        // A shimmering activity label ("Pensando", "Pesquisando na web", ...)
        // is already on screen whenever the last assistant message has a
        // pending item — a spinner below it is a redundant second indicator.
        const lastMsg: any = messageHistory[messageHistory.length - 1];
        const hasLiveActivityIndicator = !!(
          lastMsg && lastMsg.role === 'assistant' && Array.isArray(lastMsg.content) && (
            lastMsg.content.some((it: any) => it && it.pending) ||
            // The trailing thinking group holds "Pensando" through the gap
            // between the draft finishing and the answer text starting — it IS
            // the activity indicator there ("Pensou"+spinner looked broken).
            (loadingGeneration && trailingThinkingRunActive(lastMsg.content))
          )
        );
        if (hasLiveActivityIndicator) return null;
        /* Same strip wrappers as a message bubble so the spinner starts at the
           text column (it used to render bare, visibly offset from the
           "Pensando" header above and from where the answer text begins). */
        return (
          <BotMessageStrip>
            <MessageStripContentArea>
              <MessageStripTextArea>
                {extractingFiles.length > 0
                  ? <ExtractingFilesBlock files={extractingFiles} />
                  : <LoadingMessageExtracted loadingMessage={loadingMessage} />}
              </MessageStripTextArea>
            </MessageStripContentArea>
          </BotMessageStrip>
        );
      })()}

      {/* (Removed the separate pre-answer "Raciocínio" strip: reasoning blocks
          now render in-place via renderContentInOrder in the message bubble
          above — both during the "Pensando..." phase (the reasoning-only message
          renders because hasContent now counts reasoning) and while streaming.
          Keeping the old strip rendered the SAME reasoning a second time once a
          tool/text item appeared, causing duplicate "Raciocínio" boxes.) */}

      {debugMode && currentToolCalls.length > 0 && !messageBeingGenerated && (
        <BotMessageStrip>
          <MessageStripContentArea>
            <MessageStripTextArea>
              <ToolCallsDebugPanel toolCalls={currentToolCalls} isStreaming={loadingGeneration} />
            </MessageStripTextArea>
          </MessageStripContentArea>
        </BotMessageStrip>
      )}

      {deepResearchPhase && deepResearchPhase !== 'done' && deepResearchPlan.length > 0 && (
        <BotMessageStrip>
          <MessageStripContentArea>
            <MessageStripTextArea>
              <DeepResearchProgress
                phase={deepResearchPhase}
                progress={deepResearchProgress}
                steps={deepResearchSteps}
                inputTokens={deepResearchTokens.input}
                outputTokens={deepResearchTokens.output}
              />
            </MessageStripTextArea>
          </MessageStripContentArea>
        </BotMessageStrip>
      )}

      {messageBeingGenerated && (() => {
        const lastStreamingMsg = messageHistory[messageHistory.length - 1];
        const streamingFullText = getTextContent(lastStreamingMsg?.content || '');
        const allStreamingRefs = ((lastStreamingMsg as any)?.references as Reference[]) || [];
        const streamingText = streamingFullText || messageBeingGenerated || '';
        const streamingRefs = allStreamingRefs.filter(ref => ref.link && streamingText.includes(ref.link));
        // While we are actively streaming text we are by definition mid-generation.
        // (loadingGeneration can be false on the first message of a brand-new chat
        // when activeChatID has not yet synced with generatingChatId — that races
        // the streaming bubble into showing action buttons.)
        const inProgress = loadingGeneration || !!loadingMessage || !!messageBeingGenerated;
        return (
        <BotMessageStrip key="generating" className="copy-always-visible">
          <MessageStripContentArea>
            <MessageStripTextArea data-message-content>
              {renderContentInOrder(lastStreamingMsg?.content, streamingRefs, formatAssistantMessage, loadingGeneration, !!deepResearchPhase,
                !!(lastStreamingMsg as any)?.interrupted)}

              {/* Show tool status inline when text is already visible — but
                  not when a pending tool/thinking label is already shimmering
                  in the content above (one activity indicator at a time). */}
              {loadingGeneration && !(Array.isArray(lastStreamingMsg?.content) && (
                (lastStreamingMsg!.content as any[]).some((it: any) => it && it.pending) ||
                trailingThinkingRunActive(lastStreamingMsg!.content as any[])
              )) && (
                <LoadingMessageExtracted loadingMessage={loadingMessage} />
              )}
              {debugMode && currentToolCalls.length > 0 && (
                <ToolCallsDebugPanel toolCalls={currentToolCalls} isStreaming={loadingGeneration} />
              )}
              {!inProgress && (
              <ActionButtonsContainer data-download-exclude className={!inProgress ? 'copy-always-visible' : ''}>
                <CustomTooltip placement="top" title={copiedIndex === messageHistory.length - 1 ? 'Copiado!' : 'Copiar mensagem'}>
                  <ActionIconButton className="assistant-action-button copy-icon" disabled={inProgress} style={inProgress ? { opacity: 0 } : undefined} onClick={() => handleCopyToClipboard(streamingFullText || messageBeingGenerated, messageHistory.length - 1, streamingRefs)}>
                    {copiedIndex === messageHistory.length - 1 ? <CheckCircleRoundedIcon /> : <ContentCopyIcon />}
                  </ActionIconButton>
                </CustomTooltip>
                {!inProgress && (
                  <CustomTooltip placement="top" title="Gerar nova resposta">
                    <ActionIconButton className="assistant-action-button regenerate-icon" onClick={() => handleRegenerateMessage(messageHistory.length - 2)}>
                      <ReplayIcon />
                    </ActionIconButton>
                  </CustomTooltip>
                )}
                <CustomTooltip placement="top" title={(messageHistory[messageHistory.length - 1] as any)?.feedback === 'like' ? 'Remover gostei' : 'Gostei da resposta'}>
                  <ActionIconButton className="assistant-action-button like-icon" disabled={inProgress} style={inProgress ? { opacity: 0 } : undefined} onClick={() => handleLikeMessage(messageHistory.length - 1)}>
                    <ThumbUpOutlinedIcon sx={{ color: (messageHistory[messageHistory.length - 1] as any)?.feedback === 'like' ? '#4f46e5' : 'inherit' }} />
                  </ActionIconButton>
                </CustomTooltip>
                <CustomTooltip placement="top" title={(messageHistory[messageHistory.length - 1] as any)?.feedback === 'dislike' ? 'Remover feedback' : 'Não gostei da resposta'}>
                  <ActionIconButton className="assistant-action-button dislike-icon" disabled={inProgress} style={inProgress ? { opacity: 0 } : undefined} onClick={(e) => handleDislikeClick(e, messageHistory.length - 1)}>
                    <ThumbDownOutlinedIcon color={(messageHistory[messageHistory.length - 1] as any)?.feedback === 'dislike' ? 'error' : 'inherit'} />
                  </ActionIconButton>
                </CustomTooltip>
                {!inProgress && (
                  <>
                  <TextToSpeechButton content={streamingFullText || messageBeingGenerated} className="assistant-action-button" />
                  <MoreMenuWrapper>
                    <ActionIconButton className="assistant-action-button" onClick={(e) => { e.stopPropagation(); setMoreMenuIndex(moreMenuIndex === -1 ? null : -1); }}>
                      <MoreHorizIcon />
                    </ActionIconButton>
                    {moreMenuIndex === -1 && (
                      <MoreMenu onClick={(e) => e.stopPropagation()}>
                        <MoreMenuItem onClick={() => { handleShareSingleMessage(messageHistory.length - 1); setMoreMenuIndex(null); }}>
                          <MoreMenuIcon><IosShareIcon style={{ fontSize: '1.1rem' }} /></MoreMenuIcon>
                          Compartilhar
                        </MoreMenuItem>

                        <MoreMenuComponentRow onClick={(e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest('button')) { const btn = e.currentTarget.querySelector('button'); if (btn) btn.click(); } }}>
                          <DownloadResponseButton content={streamingFullText || messageBeingGenerated} />
                          Baixar resposta
                        </MoreMenuComponentRow>
                      </MoreMenu>
                    )}
                  </MoreMenuWrapper>
                  </>
                )}


              </ActionButtonsContainer>)}
            </MessageStripTextArea>

          </MessageStripContentArea>
        </BotMessageStrip>
        );
      })()}

      {chatErrorMessage && (
        <ErrorMessageExtracted
          errorMessage={chatErrorMessage}
          allowRetry={allowErrorRetry}
          showPlanUpgrade={showPlanUpgradeOption}
          onRetry={handleRetryMessage}
          onShowPlans={() => setOpenSubscriptionModal(true)}
        />
      )}

      <EndOfChatMessageMarker ref={endOfChatMessageMarkerRef} />

      <ShareModal
        open={shareModalOpen}
        onClose={() => { setShareModalOpen(false); setShareMessages(undefined); setShareMessageIndex(undefined); }}
        chatId={activeChatID?.chatID || ''}
        messagesToShare={shareMessages}
        messageIndex={shareMessageIndex}
      />
      {!loadingGeneration && <MobileBottomSpacer />}
    </>
  );
};

export default MessageList;
